// Smart Home Settings Types and Storage

export interface PhilipsHueSettings {
  enabled: boolean;
  bridgeIp: string;
  username: string;
}

export interface HomeAssistantSettings {
  enabled: boolean;
  serverUrl: string;
  accessToken: string;
}

export interface WebhookButton {
  id: string;
  name: string;
  icon: string;
  color: string;
  webhookUrl: string;
  method: "GET" | "POST";
}

export interface SmartHomeSettings {
  enabled: boolean; // Master toggle
  philipsHue: PhilipsHueSettings;
  homeAssistant: HomeAssistantSettings;
  webhookButtons: WebhookButton[];
}

const STORAGE_KEY = "smartHomeSettings";

const defaultSettings: SmartHomeSettings = {
  enabled: false,
  philipsHue: {
    enabled: false,
    bridgeIp: "",
    username: "",
  },
  homeAssistant: {
    enabled: false,
    serverUrl: "",
    accessToken: "",
  },
  webhookButtons: [],
};

export function getSmartHomeSettings(): SmartHomeSettings {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return { ...defaultSettings, ...JSON.parse(saved) };
    }
  } catch (e) {
    console.error("Failed to load smart home settings:", e);
  }
  return defaultSettings;
}

export function saveSmartHomeSettings(settings: Partial<SmartHomeSettings>): SmartHomeSettings {
  const current = getSmartHomeSettings();
  const updated = { ...current, ...settings };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

export function isSmartHomeEnabled(): boolean {
  const settings = getSmartHomeSettings();
  return settings.enabled;
}

export function addWebhookButton(button: Omit<WebhookButton, "id">): SmartHomeSettings {
  const settings = getSmartHomeSettings();
  const newButton: WebhookButton = {
    ...button,
    id: `btn-${Date.now()}`,
  };
  settings.webhookButtons.push(newButton);
  return saveSmartHomeSettings(settings);
}

export function updateWebhookButton(id: string, updates: Partial<WebhookButton>): SmartHomeSettings {
  const settings = getSmartHomeSettings();
  const index = settings.webhookButtons.findIndex((b) => b.id === id);
  if (index >= 0) {
    settings.webhookButtons[index] = { ...settings.webhookButtons[index], ...updates };
  }
  return saveSmartHomeSettings(settings);
}

export function deleteWebhookButton(id: string): SmartHomeSettings {
  const settings = getSmartHomeSettings();
  settings.webhookButtons = settings.webhookButtons.filter((b) => b.id !== id);
  return saveSmartHomeSettings(settings);
}
